import { useEffect, useState } from 'react';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { offlineSyncManager } from '../../services/offlineSyncManager';
import { WifiOff, Wifi, RefreshCw, AlertCircle } from 'lucide-react';

/**
 * Inline Offline Status Indicator Component
 * Compact version designed to fit next to weather/time widget
 */
export function InlineOfflineIndicator() {
  // Use online status hook - must be called at top level
  const { isOnline, isSupabaseOnline, isFullyOffline } = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    // Subscribe to sync status
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = offlineSyncManager.onSyncStatusChange((syncing) => {
        setIsSyncing(syncing);
      });
    } catch (error) {
      console.error('Error subscribing to sync status:', error);
    }

    // Check pending mutations periodically
    const checkPending = async () => {
      try {
        const { offlineStorage } = await import('../../services/offlineStorage');
        const pending = await offlineStorage.getPendingMutations();
        setPendingCount(pending.length);
      } catch (error) {
        console.error('Failed to check pending mutations:', error);
      }
    };

    checkPending();
    const interval = setInterval(checkPending, 5000); // Check every 5 seconds

    return () => {
      if (unsubscribe) unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    setIsSyncing(true);
    try {
      const result = await offlineSyncManager.manualSync();
      if (result.success > 0) {
        setPendingCount(prev => Math.max(0, prev - result.success));
      }
    } catch (error) {
      console.error('Manual sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 8, // Position below the element
      right: window.innerWidth - rect.right, // Calculate distance from right edge
    });
    setShowTooltip(true);
  };

  // Calculate status
  const isHealthy = !isFullyOffline && pendingCount === 0;

  return (
    <>
      <div 
        className={`relative flex items-center space-x-2 px-4 py-2 bg-slate-50 dark:bg-gray-800 rounded-lg border transition-all duration-200 ${
          isHealthy
            ? 'border-green-200 dark:border-green-800/50'
            : 'border-slate-200 dark:border-gray-700'
        }`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Status Icon */}
        <div className="flex items-center space-x-2">
          {isFullyOffline ? (
            <WifiOff className="w-4 h-4 text-red-500" />
          ) : !isSupabaseOnline ? (
            <AlertCircle className="w-4 h-4 text-yellow-500" />
          ) : (
            <Wifi className="w-4 h-4 text-green-500" />
          )}
          
          {/* Status Text */}
          <div className="text-xs">
            <div className={`font-semibold ${
              isHealthy 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-slate-800 dark:text-slate-200'
            }`}>
              {isFullyOffline ? 'Offline' : !isSupabaseOnline ? 'Limited' : 'Online'}
            </div>
          </div>

          {/* Pending Count Badge */}
          {pendingCount > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {pendingCount}
            </span>
          )}

          {/* Sync Button */}
          {pendingCount > 0 && isOnline && !isSyncing && (
            <button
              onClick={handleManualSync}
              className="p-1 hover:bg-slate-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Sync now"
            >
              <RefreshCw className="w-3 h-3 text-slate-600 dark:text-slate-300" />
            </button>
          )}

          {/* Syncing Indicator */}
          {isSyncing && (
            <div className="p-1">
              <RefreshCw className="w-3 h-3 text-blue-500 animate-spin" />
            </div>
          )}
        </div>
      </div>

      {/* Tooltip - Using fixed positioning below the indicator */}
      {showTooltip && (
        <div 
          className="fixed px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-2xl whitespace-nowrap z-[99999] pointer-events-none"
          style={{
            top: `${tooltipPosition.top}px`,
            right: `${tooltipPosition.right}px`,
          }}
        >
          <div className="flex flex-col space-y-1">
            <div>
              Network: <span className={isOnline ? 'text-green-400' : 'text-red-400'}>
                {isOnline ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <div>
              Supabase: <span className={isSupabaseOnline ? 'text-green-400' : 'text-red-400'}>
                {isSupabaseOnline ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {pendingCount > 0 && (
              <div>
                Pending: <span className="text-orange-400">{pendingCount} mutations</span>
              </div>
            )}
          </div>
          {/* Arrow pointing up to the indicator */}
          <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
        </div>
      )}
    </>
  );
}
